import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-620318f2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "image-loader" }
const _hoisted_2 = {
  key: 0,
  class: "spinner"
}
const _hoisted_3 = {
  key: 1,
  class: "image-loader-error"
}

import { onBeforeMount, provide, ref } from 'vue';
import ExpandableMedia from './ExpandableMedia.vue';

type LoadingState = 'loading' | 'failed' | 'loaded';
type ImageProps = {
  image: string;
  media: string | null | undefined;
  arweaveUrl: string | null | undefined;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ImageLoader',
  props: {
    image: null,
    media: null,
    arweaveUrl: null
  },
  setup(__props: any) {

const props = __props as {
  image: string;
  media: string | null | undefined;
  arweaveUrl: string | null | undefined;
};


const state = ref<LoadingState>('loading');
const mediaIsVideo = ref(false);
const mediaIsAudio = ref(false);
const mediaIsModel = ref(false);
const mediaIsHtml = ref(false);
const mediaAudioType = ref('');

// This get initialize on component mount
const mediaOptimized = ref();
const imageOptimized = ref();
provide('image', imageOptimized);
provide('media', mediaOptimized);
provide('mediaIsVideo', mediaIsVideo);
provide('mediaIsModel', mediaIsModel);
provide('mediaIsAudio', mediaIsAudio);
provide('mediaIsHtml', mediaIsHtml);
provide('mediaAudioType', mediaAudioType);

const onLoad = () => {
  state.value = 'loaded';
};
const onError = () => {
  state.value = 'failed';
};
provide('onLoadOnError', {
  onLoad,
  onError
});

const parseTokenMetadata = async (
  url: string,
  isTokenMedia: boolean
): Promise<string> => {
  const response = await fetch(url);

  const metadata = await response.json();
  if (isTokenMedia) {
    if (metadata.animation_url || metadata.animation) {
      return metadata.animation_url || metadata.animation;
    }
  }
  return metadata.image_url || metadata.image;
};

const getOptimizedLink = (url: string) => {
  // cloudflare does not work for cloudinary right now
  if (url.includes('cloudinary')) {
    url = url.replace('res.cloudinary.com/manifoldxyz', 'assets.manifold.xyz');
  }
  if (url.includes('assets.manifoldxyz.workers.dev')) {
    url = url.replace('assets.manifoldxyz.workers.dev', 'assets.manifold.xyz');
  }
  const urlParts = url.split('/');
  const spliceLocation = urlParts.findIndex((url) => url === 'upload');
  // url already optimized, skip optimization. Note this is only temporary to support legacy urls, until we do a full DB migration
  if (urlParts[spliceLocation + 1].includes('c_limit')) {
    return url;
  }
  urlParts.splice(spliceLocation + 1, 0, 'c_limit,q_80,w_1024');
  return urlParts.join('/');
};

const getProcessedUrl = async (
  url: string,
  isTokenMedia: boolean
): Promise<string> => {
  // show optimized link for cloudinary
  let processedUrl;
  if (
    url.includes('cloudinary') ||
    url.includes('assets.manifoldxyz.workers.dev') ||
    url.includes('assets.manifold.xyz')
  ) {
    processedUrl = getOptimizedLink(url);
  } else {
    processedUrl = url;
  }
  // account for ipfs case
  if (processedUrl.includes('ipfs')) {
    processedUrl = processedUrl.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
    processedUrl = processedUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
    // It is possible they omitted the domain
    if (processedUrl.match(/^\/ipfs\/Qm[1-9A-HJ-NP-Za-km-z]{44}$|^\/ipns\/.+/)) {
      processedUrl = `https://ipfs.io${processedUrl}`;
    }
    if (processedUrl.match(/^ipfs\/Qm[1-9A-HJ-NP-Za-km-z]{44}$/)) {
      processedUrl = `https://ipfs.io/${processedUrl}`;
    }
  }

  let response;
  try {
    response = await fetch(processedUrl, { method: 'HEAD' });
  } catch (e) {
    console.warn(e);
  }

  // Fall back to arweave if cloudinary doesn't work
  if (!response || response.status !== 200) {
    if (props.arweaveUrl) {
      const arweaveUrl = await parseTokenMetadata(
        props.arweaveUrl,
        isTokenMedia
      );
      // refetch for accurate header info
      try {
        response = await fetch(arweaveUrl, { method: 'HEAD' });
      } catch (e) {
        console.warn(e);
      }
      processedUrl = arweaveUrl;
    } else {
      processedUrl = url;
    }
  }
  // if we still don't have a response, determine if html file by looking at file extension
  if (!response) {
    const extension = processedUrl.split('.').pop();
    if (extension === 'html') {
      mediaIsHtml.value = true;
      return processedUrl;
    }
    throw new Error(`Unable to query file information for ${processedUrl}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('video')) {
    mediaIsVideo.value = true;
  }
  if (contentType?.includes('audio')) {
    mediaIsAudio.value = true;
    mediaAudioType.value = contentType;
  }
  if (contentType?.includes('model')) {
    mediaIsModel.value = true;
  }
  if (contentType?.includes('html')) {
    mediaIsHtml.value = true;
  }
  return processedUrl;
};

const initializeImageUrl = async () => {
  if (props.image) {
    imageOptimized.value = await getProcessedUrl(props.image, false);
  }
};

const initializeMediaUrl = async () => {
  if (props.media) {
    mediaOptimized.value = await getProcessedUrl(props.media, true);
  }
};

onBeforeMount(async () => {
  await initializeImageUrl();
  await initializeMediaUrl();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (state.value === 'loading')
      ? (_openBlock(), _createElementBlock("i", _hoisted_2))
      : (state.value === 'failed')
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _renderSlot(_ctx.$slots, "error")
          ]))
        : _createCommentVNode("", true),
    _createVNode(ExpandableMedia)
  ]))
}
}

})